"use client"

import { MessageSquare, Trash2 } from "lucide-react"
import { cn } from "@/lib/utils"
import type { ChatSession } from "@/types/chat.types"

function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface AppChatSessionListProps {
  sessions: ChatSession[]
  currentSessionId: string
  onSelectSession: (sessionId: string) => void
  onDeleteSession?: (sessionId: string) => void
  variant?: "panel" | "page"
  className?: string
}

export function AppChatSessionList({
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  variant = "page",
  className,
}: AppChatSessionListProps) {
  const sortedSessions = [...sessions].sort(
    (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
  )

  if (sortedSessions.length === 0) {
    return (
      <div className={cn(
        "flex h-full items-center justify-center text-center text-sm text-muted-foreground",
        className,
      )}>
        <div className="space-y-2">
          <MessageSquare className="mx-auto size-10 opacity-20" />
          <p>No chat history yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {sortedSessions.map((session) => {
        const isActive = session.id === currentSessionId
        const preview = session.messages.find((message) => message.role === "user")?.content

        return (
          <div
            key={session.id}
            className={cn(
              "group relative rounded-xl",
              variant === "panel" ? "px-1" : "px-0",
            )}
          >
            <button
              type="button"
              onClick={() => onSelectSession(session.id)}
              className={cn(
                "flex min-w-0 w-full items-start gap-2.5 rounded-xl text-left transition-colors",
                onDeleteSession && "pr-8",
                variant === "panel"
                  ? [
                      "border border-transparent px-3 py-3",
                      isActive ? "bg-muted" : "hover:bg-muted/50",
                    ]
                  : [
                      "px-3 py-2.5",
                      isActive ? "bg-background shadow-sm" : "hover:bg-background/60",
                    ],
              )}
            >
              <MessageSquare className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p
                  title={session.title}
                  className={cn(
                    "truncate text-sm",
                    isActive ? "font-medium text-foreground" : "text-foreground/80",
                  )}
                >
                  {session.title}
                </p>
                {preview && (
                  <p
                    title={preview}
                    className="mt-0.5 truncate text-[11px] text-muted-foreground"
                  >
                    {preview}
                  </p>
                )}
                <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                  {formatRelativeTime(session.updatedAt)}
                </p>
              </div>
            </button>
            {onDeleteSession && (
              <button
                type="button"
                aria-label={`Delete ${session.title}`}
                onClick={() => {
                  onDeleteSession(session.id)
                }}
                className={cn(
                  "absolute right-2 top-2 z-10 rounded-md p-1 text-muted-foreground transition-all hover:bg-red-400/10 hover:text-red-400",
                  variant === "panel"
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100",
                )}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
